import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { slugify } from "./site";

function serverPublicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

const AUTHOR_COLS =
  "id, slug, name, bio, linkedin_url, twitter_url, website_url, avatar_url, is_default, created_at, updated_at";

export const listAuthorsPublic = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = serverPublicClient();
  const { data, error } = await supabase
    .from("authors" as any)
    .select(AUTHOR_COLS)
    .order("is_default", { ascending: false })
    .order("name", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as any[];
});

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

const AuthorUpsert = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(1).max(120).optional(),
  name: z.string().min(1).max(120),
  bio: z.string().max(4000).default(""),
  linkedin_url: z.string().url().max(300).optional().nullable(),
  twitter_url: z.string().url().max(300).optional().nullable(),
  website_url: z.string().url().max(300).optional().nullable(),
  avatar_url: z.string().url().max(500).optional().nullable(),
  is_default: z.boolean().optional(),
});

export const adminListAuthors = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("authors" as any)
      .select(AUTHOR_COLS)
      .order("is_default", { ascending: false })
      .order("name", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as any[];
  });

export const adminUpsertAuthor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => AuthorUpsert.parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const slug = (data.slug && data.slug.trim()) || slugify(data.name);
    const payload = {
      slug,
      name: data.name,
      bio: data.bio ?? "",
      linkedin_url: data.linkedin_url ?? null,
      twitter_url: data.twitter_url ?? null,
      website_url: data.website_url ?? null,
      avatar_url: data.avatar_url ?? null,
    };
    // If marking this row as default, clear the current default first
    // (partial unique index enforces at most one).
    if (data.is_default) {
      const { error: clearErr } = await context.supabase
        .from("authors" as any)
        .update({ is_default: false })
        .eq("is_default", true);
      if (clearErr) throw new Error(clearErr.message);
    }

    if (data.id) {
      const { data: row, error } = await context.supabase
        .from("authors" as any)
        .update({ ...payload, ...(data.is_default !== undefined ? { is_default: data.is_default } : {}) } as any)
        .eq("id", data.id)
        .select("id, slug")
        .single();
      if (error) throw new Error(error.message);
      return row as unknown as { id: string; slug: string };
    }
    const { data: row, error } = await context.supabase
      .from("authors" as any)
      .insert({ ...payload, is_default: data.is_default ?? false } as any)
      .select("id, slug")
      .single();
    if (error) throw new Error(error.message);
    return row as unknown as { id: string; slug: string };
  });

export const adminDeleteAuthor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row } = await context.supabase
      .from("authors" as any)
      .select("is_default")
      .eq("id", data.id)
      .maybeSingle();
    if ((row as any)?.is_default) {
      throw new Error("Cannot delete the default author. Set another author as default first.");
    }
    const { error } = await context.supabase.from("authors" as any).delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminSetDefaultAuthor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error: clearErr } = await context.supabase
      .from("authors" as any)
      .update({ is_default: false })
      .eq("is_default", true);
    if (clearErr) throw new Error(clearErr.message);
    const { error } = await context.supabase
      .from("authors" as any)
      .update({ is_default: true })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
